import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../atoms/Logo';
import Button from '../atoms/Button';
import NavItem from '../molecules/NavItem';
import Icon from '../atoms/Icon';
import { useAuth } from '../../hooks/useAuth';
import useCredits from '../../hooks/useCredits';

const Header = ({
  variant = 'default',
  className = ''
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [companyFlyoutOpen, setCompanyFlyoutOpen] = useState(false);
  const [profileFlyoutOpen, setProfileFlyoutOpen] = useState(false);
  const [settingsFlyoutOpen, setSettingsFlyoutOpen] = useState(false);
  const navigate = useNavigate();
  
  const { 
    isLoggedIn, 
    currentSession, 
    logoutAll,
    getAllSessions,
    switchSession 
  } = useAuth();
  
  // Credit system (only for candidates)
  const { balance, loading: creditsLoading } = useCredits();
  
  const currentRole = currentSession?.role;
  const userEmail = currentSession?.email || '';
  
  // Force re-computation of userName when session changes
  const userName = React.useMemo(() => {
    const profile = currentSession?.profile;
    if (!profile) return userEmail;
    
    // Try full_name first
    if (profile.full_name) {
      return profile.full_name;
    }
    
    // Try first_name + last_name
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`.trim();
    }
    
    // Fallback to other fields
    return profile.contact_name || profile.username || userEmail;
  }, [
    currentSession?.profile?.full_name,
    currentSession?.profile?.first_name,
    currentSession?.profile?.last_name,
    currentSession?.profile?.contact_name,
    currentSession?.profile?.username,
    userEmail
  ]);
  
  // Debug: Log session data to understand why name is not showing
  useEffect(() => {
    console.log('🔍 Header: Current session state:', {
      isLoggedIn: isLoggedIn(),
      currentSession,
      currentRole,
      userEmail,
      userName
    });
    
    if (currentSession && currentRole === 'candidate') {
      console.log('🔍 Header Debug - Candidate Session:', {
        currentSession,
        profile: currentSession?.profile,
        full_name: currentSession?.profile?.full_name,
        first_name: currentSession?.profile?.first_name,
        last_name: currentSession?.profile?.last_name,
        contact_name: currentSession?.profile?.contact_name,
        username: currentSession?.profile?.username,
        email: currentSession?.email,
        userName: userName,
        'userName calculation': {
          'profile exists': !!currentSession?.profile,
          'full_name': currentSession?.profile?.full_name,
          'first_name': currentSession?.profile?.first_name,
          'last_name': currentSession?.profile?.last_name,
          'constructed name': currentSession?.profile?.first_name && currentSession?.profile?.last_name 
            ? `${currentSession.profile.first_name} ${currentSession.profile.last_name}`.trim()
            : 'not constructed',
          'final userName': userName
        }
      });
    }
  }, [currentSession, currentRole, userName, isLoggedIn, userEmail]);
  
  // Get all active sessions for session switcher
  const allSessions = getAllSessions();
  const hasMultipleSessions = Object.keys(allSessions).length > 1;

  const handleLogout = () => {
    console.log('🚪 Header: Logout button clicked');
    console.log('🔍 Header: All sessions before logout:', getAllSessions());
    
    // Use logoutAll to clear everything
    logoutAll();
    setProfileMenuOpen(false);
    console.log('🏠 Header: Navigating to home page');
    navigate('/');
  };

  const handleSwitchSession = (role) => {
    const switched = switchSession(role);
    if (switched) {
      setProfileMenuOpen(false);
      // Navigate to appropriate dashboard
      if (role === 'candidate') {
        navigate('/candidate/dashboard');
      } else if (role === 'hr') {
        navigate('/hr/dashboard');
      } else if (role === 'course_provider') {
        navigate('/course-provider/dashboard');
      } else if (role === 'saas_provider') {
        navigate('/saas-provider/dashboard');
      } else if (role === 'super_admin') {
        navigate('/super-admin/dashboard');
      }
    }
  };
  
  const navItems = [
    { href: '/', label: 'Home' },
    { href: '/ai-trainer', label: 'AI Trainer' },
    { href: '/courses', label: 'Courses' },
    { href: '/templates', label: 'Templates' },
    { href: '/jobs', label: 'Jobs' },
    { href: '/marketplace', label: 'Marketplace' },
    { href: '/about', label: 'About' },
  ];
  
  // Filter out Recruiter link if candidate is logged in
  const filteredNavItems = navItems.filter(item => {
    // If user is a candidate, hide the Recruiter link
    if (currentRole === 'candidate' && item.label === 'Recruiter') {
      return false;
    }
    return true;
  });
  
  const companyInsights = [
    { name: 'Google India', activeJobs: 9, keyFocus: 'Cloud • AI • Payments', locations: ['Bengaluru', 'Hyderabad'], status: 'Actively hiring' },
    { name: 'Microsoft IDC', activeJobs: 7, keyFocus: 'Security • Azure • COPILOT', locations: ['Hyderabad', 'Noida'], status: 'Interviewing' },
    { name: 'Infosys BPM', activeJobs: 5, keyFocus: 'Consulting • HR Ops', locations: ['Pune', 'Mysuru'], status: 'Talent pipeline' },
  ];

  const profileSnapshot = {
    company: 'Aptirix Digital Services Pvt Ltd',
    plan: 'Enterprise Hiring Suite',
    seatsUsed: 8,
    totalSeats: 12,
    admins: ['Arjun Mehta', 'Ritika Saha'],
    jobsActive: 6,
    interviewsThisWeek: 11,
  };

  const settingsHighlights = [
    { label: 'Notifications', description: 'ATS updates, interview reminders', action: 'Manage Alerts' },
    { label: 'Access Control', description: 'Assign recruiter / interviewer roles', action: 'Team Access' },
    { label: 'Integrations', description: 'LinkedIn, Google Workspace, Slack', action: 'View Keys' },
  ];

  const closeAllFlyouts = () => {
    setCompanyFlyoutOpen(false);
    setProfileFlyoutOpen(false);
    setSettingsFlyoutOpen(false);
  };

  if (variant === 'dashboard') {
    return (
      <header className={`sticky top-0 z-30 w-full border-b border-gray-200 dark:border-gray-700 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm ${className}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2 text-text-light-primary dark:text-text-dark-primary hover:opacity-80 transition-opacity">
                <Icon name="work" className="text-primary text-3xl" />
                <h2 className="text-xl font-bold leading-tight tracking-[-0.015em]">Aptirix</h2>
              </Link>
            </div>
            <div className="hidden md:flex flex-1 justify-center gap-8">
              <div className="flex items-center gap-4">
                <NavItem href="/hr#jobs">Jobs</NavItem>
                <NavItem href="/hr/candidates">Candidates</NavItem>
                <div className="relative">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-transparent px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                    onClick={() => {
                      setCompanyFlyoutOpen((prev) => !prev);
                      setProfileFlyoutOpen(false);
                      setSettingsFlyoutOpen(false);
                    }}
                  >
                    Companies
                    <Icon name={companyFlyoutOpen ? 'expand_less' : 'expand_more'} size={18} />
                  </button>
                  {companyFlyoutOpen && (
                    <div className="absolute top-full left-0 mt-3 w-[360px] rounded-3xl border border-gray-100 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">General Info · Jobs feed companies</p>
                      <div className="space-y-3">
                        {companyInsights.map((company) => (
                          <div key={company.name} className="rounded-2xl border border-gray-100 px-4 py-3 dark:border-gray-800">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">{company.name}</p>
                                <p className="text-xs text-gray-500">{company.status}</p>
                              </div>
                              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{company.activeJobs} roles</span>
                            </div>
                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                              <p>{company.keyFocus}</p>
                              <p className="mt-1">Locations: {company.locations.join(', ')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-primary">
                        <Link to="/companies" className="font-semibold">View company directory</Link>
                        <button type="button" onClick={closeAllFlyouts}>Close</button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-transparent px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                    onClick={() => {
                      setProfileFlyoutOpen((prev) => !prev);
                      setCompanyFlyoutOpen(false);
                      setSettingsFlyoutOpen(false);
                    }}
                  >
                    Profile
                    <Icon name={profileFlyoutOpen ? 'expand_less' : 'expand_more'} size={18} />
                  </button>
                  {profileFlyoutOpen && (
                    <div className="absolute top-full left-0 mt-3 w-[320px] rounded-3xl border border-gray-100 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Company Profile Snapshot</p>
                      <div className="rounded-2xl border border-gray-100 p-3 dark:border-gray-800">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{profileSnapshot.company}</p>
                        <p className="text-xs text-gray-500">{profileSnapshot.plan}</p>
                        <div className="mt-3 grid gap-3 text-xs text-gray-600 dark:text-gray-300">
                          <div className="flex items-center justify-between">
                            <span>Seats</span>
                            <span className="font-semibold">{profileSnapshot.seatsUsed}/{profileSnapshot.totalSeats} in use</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Admins</span>
                            <span>{profileSnapshot.admins.join(', ')}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Active jobs</span>
                            <span>{profileSnapshot.jobsActive}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Interviews (7d)</span>
                            <span>{profileSnapshot.interviewsThisWeek}</span>
                          </div>
                        </div>
                        <Link to="/hr/profile-settings" className="mt-3 inline-flex items-center text-xs font-semibold text-primary">
                          Edit Profile Settings <Icon name="chevron_right" size={16} />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-transparent px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                    onClick={() => {
                      setSettingsFlyoutOpen((prev) => !prev);
                      setCompanyFlyoutOpen(false);
                      setProfileFlyoutOpen(false);
                    }}
                  >
                    Settings
                    <Icon name={settingsFlyoutOpen ? 'expand_less' : 'expand_more'} size={18} />
                  </button>
                  {settingsFlyoutOpen && (
                    <div className="absolute top-full left-0 mt-3 w-[320px] rounded-3xl border border-gray-100 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Workspace Settings</p>
                      <div className="space-y-3 text-sm">
                        {settingsHighlights.map((item) => (
                          <div key={item.label} className="rounded-2xl border border-gray-100 px-4 py-3 dark:border-gray-800">
                            <p className="font-semibold text-gray-900 dark:text-white">{item.label}</p>
                            <p className="text-xs text-gray-500">{item.description}</p>
                            <Link to="/settings" className="mt-1 inline-flex items-center text-xs font-semibold text-primary">
                              {item.action} <Icon name="north_east" size={14} />
                            </Link>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center justify-between text-xs text-primary">
                        <button type="button" onClick={closeAllFlyouts}>Close</button>
                        <Link to="/settings" className="font-semibold">Open settings</Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button className="flex max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 bg-background-light dark:bg-background-dark text-text-light-primary dark:text-text-dark-primary gap-2 text-sm font-bold leading-normal tracking-[0.015em] min-w-0 px-2.5">
                <Icon name="notifications" />
              </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setProfileMenuOpen((prev) => !prev)}
                    className="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-10 focus:outline-none focus:ring-2 focus:ring-primary"
                    style={{ backgroundImage: 'url(https://lh3.googleusercontent.com/aida-public/AB6AXuBuz_JxAbPUTsNox7JwK_7a5S551q2FZfx1IejqGC5B_oHrmId9YgW1JP4-dUkZ2iqdqMWoWtwByU-w9hK_GBgvwqg2naZ-9yC_6sDEd7QBszJD-wiaM5lgE6oaelkRO5q8OtC-2ywG-z9PWvMfjQk6kdJauQE8gFALRbtrTZ7iDIBW3vNSBFWQTUkdb-tNTwwwmZ9MDn-9ngNhXL0BTRzOvIBDRNxGUhI897eWflI2Wr72d2c_BOFlHaZ84sE72dmcdZ10zD3YW3gD)' }}
                  />
                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-3 w-64 rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                      <div className="flex items-center gap-3 border-b border-gray-100 pb-3 dark:border-gray-800">
                        <div className="bg-center bg-no-repeat bg-cover rounded-full size-10" 
                          style={{ backgroundImage: 'url(https://lh3.googleusercontent.com/aida-public/AB6AXuBuz_JxAbPUTsNox7JwK_7a5S551q2FZfx1IejqGC5B_oHrmId9YgW1JP4-dUkZ2iqdqMWoWtwByU-w9hK_GBgvwqg2naZ-9yC_6sDEd7QBszJD-wiaM5lgE6oaelkRO5q8OtC-2ywG-z9PWvMfjQk6kdJauQE8gFALRbtrTZ7iDIBW3vNSBFWQTUkdb-tNTwwwmZ9MDn-9ngNhXL0BTRzOvIBDRNxGUhI897eWflI2Wr72d2c_BOFlHaZ84sE72dmcdZ10zD3YW3gD)' }}
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{userName || 'HR User'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userEmail || 'No email'}</p>
                        </div>
                      </div>
                      <div className="py-3 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                        <Link
                          to="/profile/view"
                          className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Icon name="person" size={18} />
                          My Profile
                        </Link>
                        
                        {/* Session Switcher */}
                        {hasMultipleSessions && (
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                            <p className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Switch Account
                            </p>
                            {Object.entries(allSessions).map(([role, session]) => (
                              <button
                                key={role}
                                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 ${
                                  role === currentRole ? 'bg-primary/10 text-primary' : ''
                                }`}
                                onClick={() => handleSwitchSession(role)}
                              >
                                <Icon name={
                                  role === 'candidate' ? 'person' :
                                  role === 'hr' ? 'business' :
                                  role === 'course_provider' ? 'school' :
                                  role === 'saas_provider' ? 'apps' :
                                  'admin_panel_settings'
                                } size={18} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {role === 'candidate' ? 'Candidate' :
                                     role === 'hr' ? 'HR' :
                                     role === 'course_provider' ? 'Course Provider' :
                                     role === 'saas_provider' ? 'SaaS Provider' :
                                     'Super Admin'}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {session.email}
                                  </p>
                                </div>
                                {role === currentRole && (
                                  <Icon name="check_circle" size={16} className="text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                          <button
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={handleLogout}
                          >
                            <Icon name="logout" size={18} />
                            Logout
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
            </div>
          </div>
        </div>
      </header>
    );
  }
  
  return (
    <header className={`sticky top-0 z-50 w-full border-b border-gray-200/50 dark:border-gray-700/50 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm ${className}`}>
      <div className="container mx-auto flex items-center justify-between whitespace-nowrap px-4 sm:px-6 lg:px-8 h-16">
        <Link to="/" className="flex items-center gap-4 text-gray-900 dark:text-white hover:opacity-80 transition-opacity">
          <Logo />
          <h2 className="text-lg font-bold tracking-tight">Aptirix</h2>
        </Link>
        
        <div className="hidden md:flex items-center justify-end gap-8">
          <nav className="flex items-center gap-9">
            {filteredNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-sm font-medium hover:text-primary dark:hover:text-primary"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {isLoggedIn() ? (
              <div className="relative">
                <button
                  className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:shadow-md transition-shadow dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  onClick={() => setProfileMenuOpen((prev) => !prev)}
                >
                  <Icon name="account_circle" size={24} />
                  <span className="max-w-[150px] truncate">{userName || userEmail || 'User'}</span>
                  <Icon name="expand_more" size={18} />
                </button>
                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-2 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-900">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                      <p className="font-semibold text-gray-900 dark:text-white truncate">{userName || userEmail}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userEmail}</p>
                      <p className="text-xs text-primary mt-1 capitalize">{currentRole?.replace('_', ' ') || 'User'}</p>
                    </div>
                    
                    {/* Credit Balance - Only for candidates */}
                    {currentRole === 'candidate' && (
                      <div className="px-3 py-3 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
                              </svg>
                            </div>
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Credits</span>
                          </div>
                          <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                            {creditsLoading ? '...' : balance}
                          </span>
                        </div>
                        {/* Progress Bar */}
                        <div className="relative w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min((balance / 150) * 100, 100)}%` }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                          {balance < 30 ? '⚠️ Low balance' : balance < 100 ? '✓ Good' : '✨ Excellent'}
                        </p>
                      </div>
                    )}
                    
                    {currentRole === 'candidate' && (
                      <>
                        <Link
                          to="/candidate/dashboard"
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Icon name="dashboard" size={18} />
                          Dashboard
                        </Link>
                        <Link
                          to="/profile/view"
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Icon name="person" size={18} />
                          My Profile
                        </Link>
                        <Link
                          to="/my-courses"
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Icon name="school" size={18} />
                          My Courses
                        </Link>
                        <Link
                          to="/jobs"
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Icon name="work" size={18} />
                          View Jobs
                        </Link>
                        <Link
                          to="/applications"
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Icon name="description" size={18} />
                          My Applications
                        </Link>
                        <Link
                          to="/my-reports"
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Icon name="assessment" size={18} />
                          My Reports
                        </Link>
                      </>
                    )}
                    {currentRole === 'hr' && (
                      <Link
                        to="/hr/dashboard"
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-gray-700 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800"
                        onClick={() => setProfileMenuOpen(false)}
                      >
                        <Icon name="dashboard" size={18} />
                        HR Dashboard
                      </Link>
                    )}
                    <div className="border-t border-gray-100 dark:border-gray-800 mt-1 pt-1">
                      <button
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                        onClick={handleLogout}
                      >
                        <Icon name="logout" size={18} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login">
                <Button variant="secondary" size="md">
                  Login
                </Button>
              </Link>
            )}
          </div>
        </div>
        
        <div className="md:hidden">
          <button 
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Icon name={mobileMenuOpen ? 'close' : 'menu'} />
          </button>
        </div>
      </div>
      
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-background-light dark:bg-background-dark">
          <nav className="flex flex-col px-4 py-4 gap-4">
            {filteredNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-sm font-medium hover:text-primary dark:hover:text-primary py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-2">
              {isLoggedIn() ? (
                <Button
                  variant="secondary"
                  size="md"
                  className="w-full"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  Logout
                </Button>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="secondary" size="md" className="w-full">
                      Login
                    </Button>
                  </Link>
                  <Link to="/login?mode=signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="primary" size="md" className="w-full">
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
      
      {/* Shimmer Animation for Credit Progress Bar */}
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </header>
  );
};

export default Header;
